/**
 * A conversation's header: who it is, what it is about, and the two calls.
 *
 * The subtitle says one thing at a time and typing wins, because typing is the
 * only one of the three that changes while you are reading it. Otherwise: a
 * channel gets `N members · topic`, a group `N members`, a person their
 * presence. A direct conversation has no members line — "2 members" for two
 * people talking is a fact nobody needed.
 *
 * The call buttons ring; the bar below this header is what the call then looks
 * like. `callId` is minted here because the first joiner names the call for
 * everybody — a later joiner's name is ignored and the stored one returned.
 */

import { useState } from 'react';
import { ds } from '../../ds';
import { useRouter } from '../../router';
import { useI18n, useT } from '../../i18n';
import { useStore } from '../../lib/relay/store';
import { useCalls } from '../../state/calls';
import { PanelHeader, useNarrow } from '../Shell';
import { Avatar } from '../Avatar';
import { FilesDialog } from './FilesDialog';
import { MembersDialog } from './MembersDialog';
import { VerifiedMark } from './Official';
import type { Conversation } from '../../lib/relay/types';
import './chat.css';

export function ConversationHeader({ conversation }: { conversation: Conversation }) {
  const { Icon, IconButton } = ds();
  const t = useT();
  const { tn } = useI18n();
  const narrow = useNarrow();
  const { navigate } = useRouter();

  const me = useStore((s) => s.me());
  const typing = useStore((s) => s.typing(conversation.id));
  const other = conversation.kind === 'direct'
    ? conversation.members.find((id) => id !== me?.id)
    : undefined;
  const presence = useStore((s) => (other ? s.presenceOf(other) : 'offline'));
  // The face beside the name, for a conversation that has one.
  const people = useStore((s) => s.people(other ? [other] : []));
  const person = people[0];

  const [members, setMembers] = useState(false);
  const [files, setFiles] = useState(false);

  const subtitle = (() => {
    if (typing.length === 1) return t('conversation.typing.one', { name: typing[0]!.name });
    if (typing.length > 1) return t('conversation.typing.other', { count: typing.length });
    if (conversation.kind === 'direct') return t(`conversation.presence.${presence}`);
    if (conversation.kind === 'self') return '';
    const count = tn('conversation.members', conversation.members.length);
    return conversation.topic ? `${count} · ${conversation.topic}` : count;
  })();

  const calls = useCalls();
  // Ringing and taking a seat are one act, and both of them belong to the one
  // thing that owns a call. A second `ring` from here would be a second call id
  // for a call that already has one.
  const ring = (camera: boolean) => calls.start(conversation.id, { camera });
  const busy = calls.active !== null;

  const roomy = conversation.kind === 'group' || conversation.kind === 'channel';
  // Mauvely's own conversation: nobody to call, no members to manage, nothing
  // to rename. The server refuses all of it; this does not offer it.
  const official = conversation.official === true;

  return (
    <>
      <PanelHeader
        lead={
          <>
            {narrow && (
              <IconButton icon="ArrowLeft" label={t('chrome.back')} onClick={() => navigate('/')} />
            )}
            {person ? (
              <Avatar person={person} size={28} presence={presence} />
            ) : (
              conversation.kind === 'channel' && <Icon name="Channel" size={16} />
            )}
          </>
        }
        title={
          official ? (
            <>
              {conversation.title}
              <VerifiedMark />
            </>
          ) : (
            conversation.title
          )
        }
        subtitle={official ? '' : subtitle}
        actions={
          official ? undefined : <>
            <IconButton
              icon="Call"
              label={t('conversation.call')}
              disabled={busy}
              onClick={() => ring(false)}
            />
            <IconButton
              icon="VideoCall"
              label={t('conversation.video')}
              disabled={busy}
              onClick={() => ring(true)}
            />
            <IconButton
              icon="RailFiles"
              label={t('conversation.files')}
              onClick={() => setFiles(true)}
            />
            {roomy && (
              <IconButton
                icon="People"
                label={t('conversation.members.open')}
                onClick={() => setMembers(true)}
              />
            )}
          </>
        }
      />

      {files && <FilesDialog conversation={conversation} onClose={() => setFiles(false)} />}

      {members && (
        <MembersDialog
          conversation={conversation}
          onClose={() => setMembers(false)}
          onLeft={() => navigate('/')}
          onDeleted={() => navigate('/')}
        />
      )}
    </>
  );
}
